import {
    CvtStatusData,
    GetCVTsResponse,
    CvtUploadResponse,
    CvtSbrResponseData,
    CvtPresignedUrlsUploadFailure,
} from '@packages/models/api/cvt';
import { HttpClient } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';

const logger = getLogger('cvt');

export class CvtService {
    constructor(private http: HttpClient) {}

    getCvtList(vin: string, queryString?: string, ignoreCache: boolean = true) {
        return this.http.orchestrateRequest<GetCVTsResponse>({
            method: 'GET',
            url: `/cvt/v2/${vin}?${queryString}`,
            ignoreCache,
        });
    }

    deleteCvt(diagnosticId: string) {
        return this.http.orchestrateRequest({
            method: 'DELETE',
            url: `/cvt/${diagnosticId}`,
        });
    }

    uploadFile(vin: string, repairOrderNumber: string) {
        return this.http.orchestrateRequest<CvtUploadResponse>({
            method: 'POST',
            url: `/cvt/upload/v2/${vin}/${repairOrderNumber}`,
        });
    }

    runDiagnostic(
        stsDiagnosticId: string,
        sbrDiagnosticId: string,
        uploadFailureStatus: CvtPresignedUrlsUploadFailure | {}
    ) {
        return this.http.orchestrateRequest({
            method: 'POST',
            url: `/cvt/send/v2/${stsDiagnosticId}/${sbrDiagnosticId}`,
            body: uploadFailureStatus,
        });
    }

    getCvtStatus(diagnosticId: string, ignoreCache: boolean = true, signal?: AbortSignal) {
        return this.http.orchestrateRequest<CvtStatusData>({
            method: 'GET',
            url: `/cvt/status/v2/${diagnosticId}`,
            ignoreCache,
            signal,
        });
    }

    async uploadFileToS3(file: File, preSignedUploadUrl: string): Promise<void> {
        try {
            const response = await fetch(preSignedUploadUrl, {
                method: 'PUT',
                body: file,
            });
            if (!response.ok) {
                const errorMessage = `${response.status} ${response.statusText}`;
                logger.error(`Failed to upload the CVT file to S3 [${response.status} ${response.statusText}]`, {
                    statusCode: response.status,
                    statusText: response.statusText,
                });
                throw new Error(errorMessage);
            }
        } catch (error) {
            const errorMessage = 'Error uploading file to presigned URL:';
            logger.error('Failed to upload the CVT file to the presigned URL', { error });
            throw new Error(errorMessage, { cause: error });
        }
    }

    async fetchSbrResponseFromS3(sbrResponseDataUrl: string): Promise<CvtSbrResponseData> {
        try {
            const response = await fetch(sbrResponseDataUrl);
            if (!response.ok) {
                const errorMessage = `Failed to fetch SBR from presigned URL: ${response.status} ${response.statusText}`;
                logger.error(`Failed to fetch the SBR response from S3 [${response.status} ${response.statusText}]`, {
                    statusCode: response.status,
                    statusText: response.statusText,
                });
                throw new Error(errorMessage);
            }
            return (await response.json()) as CvtSbrResponseData;
        } catch (error) {
            const errorMessage = 'Error fetching SBR from presigned URL:';
            logger.error('Failed to fetch the SBR response from the presigned URL', { error });
            throw new Error(errorMessage);
        }
    }
}
